import { FC, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import EditIcon from '@material-ui/icons/Edit';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/formatDate';
import StatusBadge from './StatusBadge';
import SourceBadge from './SourceBadge';
import MetadataEditor from './MetadataEditor';
import { TEXT_MAX, charCount, type MetadataValues } from './metadataValidation';
import {
  canDeleteThread,
  canEditThreadContent,
  isMergeLockedSource,
  isTerminalThreadStatus,
  type RequirementInstance,
  type RequirementThread,
  type ThreadStatus,
} from './types';

/** How a resolved lineage relative relates to the thread being viewed. */
export type LineageRelation =
  | 'parent'
  | 'splitChild'
  | 'mergedInto'
  | 'mergeSource';

/** A resolved lineage relative (parent / child / merge target / merge source). */
export interface LineageRef {
  threadId: string;
  name: string;
  status: ThreadStatus;
  relation: LineageRelation;
}

interface ThreadDetailProps {
  thread: RequirementThread;
  instances: RequirementInstance[];
  /** Resolved lineage relatives for display (names + navigation) */
  lineage: LineageRef[];
  /** The role may edit, submit, split and review proposals (collaborator and above). */
  canWrite: boolean;
  /** The role may approve, force-approve, un-approve and delete (maintainer and above). */
  canApprove: boolean;
  busy: boolean;
  onBack: () => void;
  onEdit: (text: string) => Promise<boolean>;
  /** Save changed metadata fields. Creates no text version. */
  onUpdateMetadata: (patch: Partial<MetadataValues>) => Promise<boolean>;
  onAccept: (versionId: string) => Promise<boolean>;
  onReject: (versionId: string) => Promise<boolean>;
  onSubmit: () => Promise<boolean>;
  onApprove: () => Promise<boolean>;
  onForceApprove: () => Promise<boolean>;
  /** Reverse an approval (APPROVED → WIP). Only shown when thread is APPROVED. */
  onUnapprove: () => Promise<boolean>;
  onDelete: () => void;
  onSplit: () => void;
  /** Open the pending Split review for this PENDING_SPLIT parent */
  onReviewSplit: () => void;
  /** Open the pending Merge review: this PENDING_MERGE target's, or the one this locked source is in */
  onReviewMerge: () => void;
  /** Navigate to another thread (lineage link) */
  onOpenThread: (threadId: string) => void;
}

const useStyles = makeStyles(theme => ({
  card: { marginBottom: theme.spacing(1.5) },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(0.5),
  },
  title: { fontSize: 18, fontWeight: 700, color: theme.palette.text.primary },
  idBadge: { fontSize: 12, color: theme.palette.text.secondary },
  meta: { fontSize: 12, color: theme.palette.text.secondary },
  actions: { display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap' },
  terminalBanner: { marginTop: theme.spacing(1.25) },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    margin: theme.spacing(1.5, 0, 1),
  },
  inlineText: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 3,
    padding: theme.spacing(1.5),
    whiteSpace: 'pre-wrap',
    marginBottom: theme.spacing(1),
  },
  diff: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1),
    margin: theme.spacing(1, 0),
  },
  diffOld: {
    background: theme.palette.error.light,
    borderRadius: 3,
    padding: theme.spacing(1),
  },
  diffNew: {
    background: theme.palette.success.light,
    borderRadius: 3,
    padding: theme.spacing(1),
  },
  diffLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    marginBottom: 4,
  },
  collapseHeader: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontWeight: 600,
    color: theme.palette.text.primary,
    padding: theme.spacing(0.5, 0),
  },
  versionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  versionNum: { fontWeight: 700, minWidth: 56 },
  versionMeta: { fontSize: 12, color: theme.palette.text.secondary },
  pendingCard: {
    background: theme.palette.warning.light,
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: 3,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  reviewActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  lineageLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    fontWeight: 600,
    '&:hover': { textDecoration: 'underline' },
  },
  lineageRelation: {
    fontWeight: 700,
    minWidth: 96,
    color: theme.palette.text.secondary,
  },
  tagChip: {
    height: 20,
    fontSize: 11,
    background: theme.palette.action.selected,
    color: theme.palette.text.secondary,
  },
  metaKey: {
    fontWeight: 700,
    color: theme.palette.text.secondary,
    marginRight: 6,
  },
  bold: { fontWeight: 700 },
  description: { marginTop: theme.spacing(0.5) },
  tags: {
    display: 'flex',
    gap: 6,
    marginTop: 6,
    flexWrap: 'wrap',
  },
  editDetails: { marginTop: theme.spacing(0.5) },
  // Full width while the form is open, so it does not resize as messages
  // appear under the fields.
  headerMainEditing: { width: '100%' },
}));

const TERMINAL_SEVERITY: Record<
  string,
  'success' | 'error' | 'warning' | 'info'
> = {
  APPROVED: 'success',
  DELETED: 'error',
  SPLIT: 'warning',
  MERGED: 'info',
};

export const ThreadDetail: FC<ThreadDetailProps> = ({
  thread,
  instances,
  lineage,
  canWrite,
  canApprove,
  busy,
  onBack,
  onEdit,
  onUpdateMetadata,
  onAccept,
  onReject,
  onSubmit,
  onApprove,
  onForceApprove,
  onUnapprove,
  onDelete,
  onSplit,
  onReviewSplit,
  onReviewMerge,
  onOpenThread,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('requirement-lifecycle');

  // Prefer a true CURRENT version; fall back to APPROVED. Using two passes
  // (rather than one OR-find) guarantees CURRENT wins regardless of array order.
  const current = useMemo(
    () =>
      instances.find(i => i.status === 'CURRENT') ??
      instances.find(i => i.status === 'APPROVED'),
    [instances],
  );
  const pending = useMemo(
    () => instances.find(i => i.status === 'PENDING_REVIEW'),
    [instances],
  );

  const currentText = current?.text ?? '';

  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editError, setEditError] = useState(false);
  const [editingMetadata, setEditingMetadata] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [lineageOpen, setLineageOpen] = useState(true);

  // Reset edit state when the displayed thread changes to avoid stale content.
  useEffect(() => {
    setEditing(false);
    setEditText('');
    setEditError(false);
    setEditingMetadata(false);
  }, [thread.threadId]);

  const status: ThreadStatus = thread.status;
  const terminal = isTerminalThreadStatus(status);
  // A source of an open Merge stays WIP but is locked: nothing on
  // it can change until the Merge is accepted or rejected.
  const mergeLocked = isMergeLockedSource(thread);
  // Text and metadata: the PENDING_MERGE target can be revised during the review.
  const editable = canEditThreadContent(thread);
  const deletable = canDeleteThread(thread);
  // A thread sitting on a pending Split/Merge can be reviewed at any time, not
  // just inline right after Propose
  const pendingSplit = status === 'PENDING_SPLIT';
  const pendingMerge = status === 'PENDING_MERGE' || mergeLocked;
  // Approving (or force-approving) a thread that still has an unresolved
  // Split/Merge pointing at it would strand those pending children/sources —
  // the backend already rejects this, so mirror the same check here to
  // disable the buttons instead of surfacing a backend error.
  // A Merge target is PENDING_MERGE until its proposal resolves, so its status
  // already rules approval out; a locked source is blocked by mergeLocked.
  const hasPendingSplitChildren = lineage.some(
    rel => rel.relation === 'splitChild' && rel.status === 'PENDING_SPLIT',
  );
  const hasUnresolvedLineage = hasPendingSplitChildren || mergeLocked;
  // Shared by both approval actions below, so each button's `disabled` only
  // has to add its own status check on top of this.
  // Named broadly, not has<Specific> like the lineage checks above, because it
  // deliberately aggregates unrelated reasons (async state, permission, lineage).
  const hasApprovalBlocker = busy || !canApprove || hasUnresolvedLineage;

  const startEdit = () => {
    setEditText(currentText);
    setEditError(false);
    setEditing(true);
  };

  const trimmedEdit = editText.trim();
  const editChanged = trimmedEdit !== currentText.trim() && trimmedEdit !== '';
  // Saved trimmed (saveEdit), so the limit applies to the trimmed text. The
  // Requirement Doctor's editor sends the text as typed and checks it untrimmed.
  const editTooLong = charCount(trimmedEdit) > TEXT_MAX;

  const saveEdit = async () => {
    setEditError(false);
    const ok = await onEdit(trimmedEdit);
    if (ok) {
      setEditing(false);
    } else {
      setEditError(true);
    }
  };

  const saveMetadata = async (patch: Partial<MetadataValues>) => {
    const ok = await onUpdateMetadata(patch);
    if (ok) setEditingMetadata(false);
    return ok;
  };

  return (
    <Box>
      {/* Breadcrumb */}
      <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
        <Button
          size="small"
          color="primary"
          onClick={onBack}
          style={{ minWidth: 0, padding: '0 4px', textTransform: 'none' }}
        >
          {t('detail.breadcrumb.list')}
        </Button>
        <Typography variant="body2" color="textSecondary">
          {' / '}
          {thread.name || `#${thread.threadId}`}
        </Typography>
      </Box>

      {/* Header */}
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.headerRow}>
            <Box
              className={
                editingMetadata ? classes.headerMainEditing : undefined
              }
            >
              <Typography className={classes.title}>
                {thread.name || `#${thread.threadId}`}
              </Typography>
              <Box className={classes.titleRow}>
                <span className={classes.idBadge}>#{thread.threadId}</span>
                <StatusBadge status={status} />
                {current && current.versionNumber > 0 && (
                  <span className={classes.idBadge}>
                    {t('detail.version', { n: current.versionNumber })}
                  </span>
                )}
              </Box>
              {editingMetadata ? (
                <MetadataEditor
                  thread={thread}
                  busy={busy}
                  onSave={saveMetadata}
                  onCancel={() => setEditingMetadata(false)}
                />
              ) : (
                <>
                  <Typography className={classes.meta}>
                    {t('common.updatedAt')}: {formatDateTime(thread.updatedAt)}
                    {thread.externalRef ? ` · ${thread.externalRef}` : ''}
                    {thread.owner
                      ? ` · ${t('common.owner')}: ${thread.owner}`
                      : ''}
                  </Typography>
                  {thread.description && (
                    <Typography
                      className={`${classes.meta} ${classes.description}`}
                    >
                      {thread.description}
                    </Typography>
                  )}
                  {thread.tags.length > 0 && (
                    <Box className={classes.tags}>
                      {thread.tags.map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          className={classes.tagChip}
                        />
                      ))}
                    </Box>
                  )}
                  {/* Same rule as the text: DRAFT and WIP (not a locked Merge
                      source), and the PENDING_MERGE target under review. */}
                  {canWrite && editable && (
                    <Button
                      size="small"
                      className={classes.editDetails}
                      startIcon={<EditIcon fontSize="small" />}
                      disabled={busy}
                      onClick={() => setEditingMetadata(true)}
                    >
                      {t('detail.metadata.edit')}
                    </Button>
                  )}
                </>
              )}
            </Box>

            <Box className={classes.actions}>
              {/* Submit (DRAFT → WIP) — the entry to the review lifecycle. */}
              {canWrite && status === 'DRAFT' && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={busy}
                  onClick={onSubmit}
                >
                  {t('detail.actions.submit')}
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={status !== 'WIP' || hasApprovalBlocker}
                onClick={onApprove}
              >
                {t('detail.actions.approve')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={
                  (status !== 'DRAFT' && status !== 'WIP') || hasApprovalBlocker
                }
                onClick={onForceApprove}
              >
                {t('detail.actions.forceApprove')}
              </Button>
              {status === 'APPROVED' && canApprove && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={busy}
                  onClick={onUnapprove}
                >
                  {t('detail.actions.unapprove')}
                </Button>
              )}
              {canApprove && deletable && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  disabled={busy}
                  onClick={onDelete}
                >
                  {t('detail.actions.delete')}
                </Button>
              )}
              {/* Split is available from WIP, except on a locked Merge source */}
              {canWrite && status === 'WIP' && !mergeLocked && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={busy}
                  onClick={onSplit}
                >
                  {t('detail.actions.split')}
                </Button>
              )}
              <Button variant="outlined" size="small" disabled>
                {t('detail.actions.analyse')}
              </Button>
            </Box>
          </Box>

          {terminal && (
            <Alert
              severity={TERMINAL_SEVERITY[status] ?? 'info'}
              className={classes.terminalBanner}
            >
              {t(`detail.terminal.${status}`)}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Pending Split/Merge review re-entry: a thread parked on a
          pending lineage operation can be reviewed any time, not just inline
          right after Propose. */}
      {(pendingSplit || pendingMerge) && (
        <Box className={classes.pendingCard}>
          <Typography className={classes.bold}>
            {t(
              pendingSplit
                ? 'detail.pendingLineage.splitTitle'
                : 'detail.pendingLineage.mergeTitle',
            )}
          </Typography>
          <Typography className={classes.versionMeta}>
            {t('detail.pendingLineage.hint')}
          </Typography>
          <Box className={classes.reviewActions}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={busy}
              onClick={pendingSplit ? onReviewSplit : onReviewMerge}
            >
              {t('detail.pendingLineage.review')}
            </Button>
          </Box>
        </Box>
      )}

      {/* PENDING_REVIEW banner */}
      {pending && (
        <Box className={classes.pendingCard}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography className={classes.bold}>
              {t('detail.pending.title')}
            </Typography>
            <SourceBadge source={pending.source} />
          </Box>
          <Typography className={classes.versionMeta}>
            {t('detail.pending.instanceId', { id: pending.versionId })}
            {pending.versionNumber > 0
              ? ` · ${t('detail.version', { n: pending.versionNumber })}`
              : ''}
            {pending.createdAt
              ? ` · ${t('detail.pending.createdAt', {
                  time: formatDateTime(pending.createdAt),
                })}`
              : ''}
          </Typography>
          <Box className={classes.diff}>
            <Box className={classes.diffOld}>
              <div className={classes.diffLabel}>
                {t('detail.pending.diffOld')}
              </div>
              {currentText || '—'}
            </Box>
            <Box className={classes.diffNew}>
              <div className={classes.diffLabel}>
                {t('detail.pending.diffNew')}
              </div>
              {pending.text}
            </Box>
          </Box>
          {canWrite && (
            <Box className={classes.reviewActions}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={busy}
                onClick={() => onAccept(pending.versionId)}
              >
                {t('detail.pending.accept')}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                disabled={busy}
                onClick={() => onReject(pending.versionId)}
              >
                {t('detail.pending.reject')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Current instance text + inline edit */}
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.sectionTitle}>{t('detail.currentText')}</div>
          {editing ? (
            <Box>
              <TextField
                fullWidth
                multiline
                minRows={4}
                variant="outlined"
                value={editText}
                onChange={e => setEditText(e.target.value)}
                disabled={busy}
                error={editTooLong}
                helperText={
                  editTooLong
                    ? t('detail.textTooLong', { max: TEXT_MAX })
                    : undefined
                }
              />
              <Typography
                className={classes.versionMeta}
                style={{ margin: '6px 0' }}
              >
                {t('detail.editHint')}
              </Typography>
              {editError && (
                <Alert severity="error">{t('detail.editError')}</Alert>
              )}
              <Box className={classes.reviewActions}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={busy || !editChanged || editTooLong}
                  onClick={saveEdit}
                  endIcon={busy ? <CircularProgress size={18} /> : undefined}
                >
                  {t('detail.actions.save')}
                </Button>
                <Button
                  size="small"
                  disabled={busy}
                  onClick={() => setEditing(false)}
                >
                  {t('detail.actions.cancel')}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <div className={classes.inlineText}>{currentText || '—'}</div>
              {canWrite && editable && !pending && (
                <Button
                  size="small"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={startEdit}
                >
                  {t('detail.actions.edit')}
                </Button>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Version history */}
      <Card className={classes.card}>
        <CardContent>
          <div
            className={classes.collapseHeader}
            onClick={() => setHistoryOpen(o => !o)}
            role="button"
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter') setHistoryOpen(o => !o);
            }}
          >
            {historyOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            {t('detail.versionHistory')}
          </div>
          <Collapse in={historyOpen}>
            <Divider />
            <Box mt={1}>
              {instances
                .slice()
                .reverse()
                .map(inst => {
                  const metaKeys = Object.keys(inst.metadata);
                  return (
                    <Box key={inst.versionId} mb={0.5}>
                      <Box className={classes.versionItem}>
                        <span className={classes.versionNum}>
                          {inst.versionNumber > 0
                            ? t('detail.version', { n: inst.versionNumber })
                            : `#${inst.versionId}`}
                        </span>
                        <StatusBadge status={inst.status} />
                        <SourceBadge source={inst.source} />
                        <span className={classes.versionMeta}>
                          {formatDateTime(inst.createdAt || inst.updatedAt)}
                        </span>
                      </Box>
                      {metaKeys.length > 0 && (
                        <Box style={{ paddingLeft: 56 }}>
                          {metaKeys.map(key => (
                            <Typography
                              key={key}
                              className={classes.versionMeta}
                            >
                              <span className={classes.metaKey}>{key}:</span>
                              {inst.metadata[key]}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Branch Hierarchy / lineage — resolved
          relatives rendered with names and navigation links. Collapsible */}
      {lineage.length > 0 && (
        <Card className={classes.card}>
          <CardContent>
            <div
              className={classes.collapseHeader}
              onClick={() => setLineageOpen(o => !o)}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter') setLineageOpen(o => !o);
              }}
            >
              {lineageOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              {t('detail.lineage')}
            </div>
            <Collapse in={lineageOpen}>
              <Divider />
              <Box mt={1}>
                {lineage.map(rel => (
                  <Box
                    key={`${rel.relation}-${rel.threadId}`}
                    className={classes.versionItem}
                  >
                    <span className={classes.lineageRelation}>
                      {t(`detail.lineageLabels.${rel.relation}`)}
                    </span>
                    <span
                      className={classes.lineageLink}
                      onClick={() => onOpenThread(rel.threadId)}
                      role="link"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter') onOpenThread(rel.threadId);
                      }}
                    >
                      {rel.name || `#${rel.threadId}`}
                    </span>
                    <StatusBadge status={rel.status} />
                    <span className={classes.versionMeta}>#{rel.threadId}</span>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ThreadDetail;
